import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import C from 'constants';
import {
  get,
  find,
  isEqual,
} from 'lodash';
import withRouter from '../../../wrappers/withRouter';
import {
  tablePropsType,
  tablePropsDefault,
} from '../../../types/tablePropsType';
import {
  getSettings,
  getFiltersFromUrl,
  getData,
} from '../../../helpers/Table';
import {
  getParam,
} from '../../../helpers/Navigation';
import { list as adProviderList } from '../../../helpers/AdProvider';
import { list as orderTypeList } from '../../../helpers/OrderType';
import {
  getCurrencies,
} from '../../../actions/Currency/CurrencyActions';
import API from '../../../api';
import VibeTable from '../VibeTable';
import VibeModal from '../../VibeModal/VibeModal';
import VibeButton from '../../VibeButton/VibeButton';
import CellTooltip from '../CellTypes/CellTooltip';
import LabelCell from '../CellTypes/LabelCell';
import TextCell from '../CellTypes/TextCell';
import TimestampCell from '../CellTypes/TimestampCell';
import ApproversCell from '../CellTypes/ApproversCell';
import FlightStatus from '../../FlightStatus/FlightStatus';
import MissingCreatives from '../../MissingCreatives/MissingCreatives';
import viEye from '../../../icons/viEye';
import * as color from '../../../sass/color.scss';
import './TableFlights.scss';

const tableId = 'table:flights';
const permissionPrefix = 'flight';

class TableFlights extends PureComponent {
  constructor(props) {
    super(props);

    const {
      columns: columnNames,
      defaultSortBy,
      pageSize,
      paginatorProps: {
        urlPaging,
        urlFilters,
      },
      currencies,
    } = props;

    const columnsDef = [{
      name: '.',
      defaultWidth: 40,
    },
    {
      name: 'Select',
      defaultWidth: 125,
      locked: true,
    },
    {
      name: 'Name',
      searchAttr: 'name',
      defaultWidth: 275,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
      sortable: true,
      autoFocus: true,
    },
    {
      name: `${C.PROPOSAL} Type`,
      searchAttr: 'orderType',
      defaultWidth: 270,
      searchable: true,
      resizable: true,
      dropdownItems: orderTypeList({
        includeEmpty: true,
        emptyData: {
          placeholder: 'Search...',
        },
      }),
    },
    {
      name: 'Owner/Partner',
      searchAttr: 'partnerName',
      defaultWidth: 225,
      resizable: true,
    },
    {
      name: 'Status',
      searchAttr: 'status',
      defaultWidth: 170,
      searchable: true,
      sortable: true,
      resizable: true,
      dropdownItems: [
        {
          label: '',
          value: '',
          placeholder: 'Search...',
        },
        {
          label: 'Unconfirmed',
          value: 'unconfirmed',
        },
        {
          label: 'Confirming Queued',
          value: 'confirming-queued',
        },
        {
          label: 'Computing Queued',
          value: 'computing-queued',
        },
        {
          label: 'Confirming',
          value: 'confirming',
        },
        {
          label: 'Computing',
          value: 'computing',
        },
      ],
    },
    {
      name: 'Creative',
      searchAttr: 'missingCreatives',
      defaultWidth: 170,
      searchable: true,
      sortable: true,
      resizable: true,
      missingCreatives: true,
      dropdownItems: [
        {
          label: '',
          value: '',
          placeholder: 'Search...',
        },
        {
          label: 'Missing',
          value: true,
        },
        {
          label: 'Attached',
          value: false,
        },
        {
          label: 'N/A',
          value: 'N/A',
        },
      ],
    },
    {
      name: 'Opportunity Name',
      searchAttr: 'opportunityName',
      defaultWidth: 275,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
      sortable: true,
    },
    {
      name: 'Opportunity ID',
      searchAttr: 'opportunityId',
      defaultWidth: 225,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
    },
    {
      name: 'Deal ID',
      searchAttr: 'dealId',
      defaultWidth: 225,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
      sortable: true,
    },
    {
      name: 'Company',
      searchAttr: 'companyId',
      sortAttr: 'companyName',
      valueAttr: 'companies',
      defaultWidth: 190,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
      searchSelector: 'company',
      sortable: false,
    },
    {
      name: 'Order Reference ID',
      searchAttr: 'extReferenceId',
      defaultWidth: 225,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
      sortable: true,
    },
    {
      name: 'Delivery System',
      searchAttr: 'deliverySystem',
      defaultWidth: 190,
      resizable: true,
      searchable: true,
      searchSelector: 'delivery-system',
      sortable: true,
    },
    {
      name: 'Ad Provider',
      searchAttr: 'adProvider',
      defaultWidth: 190,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: adProviderList({
        includeEmpty: true,
        emptyData: {
          placeholder: 'Search...',
        },
      }),
    },
    {
      name: 'Advertiser',
      searchAttr: 'advertiserName',
      defaultWidth: 230,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
      searchSelector: 'advertiser',
      sortable: true,
    },
    {
      name: 'Ad Category',
      searchAttr: 'category',
      defaultWidth: 230,
      resizable: true,
      searchable: true,
      searchAdvanced: true,
      searchSelector: 'category',
      sortable: true,
    },
    {
      name: 'Approver',
      // searchAttr: '',
      defaultWidth: 180,
      resizable: true,
      searchable: false,
    },
    {
      name: 'Approver Setting',
      // searchAttr: '',
      defaultWidth: 245,
      resizable: true,
      searchable: false,
    },
    {
      name: 'Currency',
      searchAttr: 'currencyCode',
      defaultWidth: 190,
      resizable: true,
      searchable: true,
      sortable: true,
      dropdownItems: [
        {
          label: '',
          value: '',
          placeholder: 'Search...',
        },
        ...currencies.map(currency => {
          return {
            label: currency.label,
            value: currency.key,
          };
        }),
      ],
    },
    {
      name: 'Start Date',
      searchAttr: 'startDate',
      defaultWidth: 175,
      datepicker: true,
      searchable: true,
      sortable: true,
      resizable: true,
    },
    {
      name: 'End Date',
      searchAttr: 'endDate',
      defaultWidth: 175,
      datepicker: true,
      searchable: true,
      sortable: true,
      resizable: true,
    },
    {
      name: 'Created By',
      searchAttr: 'createdByUserName',
      defaultWidth: 175,
      resizable: true,
      searchable: true,
      sortable: true,
    },
    {
      name: 'Created Date',
      searchAttr: 'createdDate',
      defaultWidth: 175,
      datepicker: true,
      searchable: true,
      sortable: true,
      resizable: true,
    },
    {
      name: '...',
      defaultWidth: 72,
    }];

    const settings = getSettings({
      tableId,
      columnsDef,
      columnNames,
    });

    const creativesFilters = getFiltersFromUrl({ columns: columnsDef });
    const updatedFilters = getFiltersFromUrl({ columns: columnsDef });

    if (creativesFilters.missingCreatives === 'N/A') {
      updatedFilters.adProvider = adProviderList().filter(item => item.value !== 'vibenomics').map(item => item.value);
    }

    if (creativesFilters.missingCreatives !== undefined && creativesFilters.missingCreatives !== 'N/A') {
      updatedFilters.adProvider = 'vibenomics';
      updatedFilters.missingCreatives = creativesFilters.missingCreatives;
    }

    this.state = {
      columnsDef,
      columnNames,
      rows: [],
      loading: true,
      totalItems: 0,
      active: !urlFilters || (urlFilters && getParam('active') !== 'false'),
      pageNumber: urlPaging
        ? parseInt(getParam('page') || 1, 10)
        : 1,
      pageSize: get(settings, 'pageSize', pageSize),
      sortBy: {
        label: get(settings, 'sortBy.label', defaultSortBy.label),
        attr: get(settings, 'sortBy.attr', defaultSortBy.attr),
        direction: get(settings, 'sortBy.direction', defaultSortBy.direction),
      },
      filters: urlFilters
        ? updatedFilters
        : {},
      confirm: false,
      confirmRow: {},
      confirmAction: '',
      confirmTitle: '',
      confirmText: '',
      confirmApproveColor: '',
      // only load currencies if the column exists
      currenciesFetched: !columnNames.includes('Currency') || currencies.length > 0,
    };

    // listen for when sidebar data changes
    document.addEventListener('onSaveFlight', this.onUpdateTableData);
  }

  componentDidMount() {
    const {
      getCurrencies,
    } = this.props;

    this.getData();

    // get supported currencies
    getCurrencies();
  }

  componentDidUpdate(prevProps) {
    const {
      fetch,
      collection,
      currencies,
    } = this.props;

    const {
      collection: prevCollection,
      currencies: prevCurrencies,
    } = prevProps;

    if (!fetch && !isEqual(collection, prevCollection)) {
      this.onUpdate({
        refresh: true,
      });
    }

    // Currencies fetched, update the search dropdown
    if (currencies.length > 0 && prevCurrencies.length <= 0) {
      this.setState((state) => {
        const {
          columnsDef,
        } = state;

        const currencyColumn = find(columnsDef, { name: 'Currency' });
        currencyColumn.dropdownItems = [
          {
            label: '',
            value: '',
            placeholder: 'Search...',
          },
          ...currencies.map(currency => {
            return {
              label: currency.label,
              value: currency.key,
            };
          }),
        ];

        return {
          columnsDef,
          currenciesFetched: true,
        };
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('onSaveFlight', this.onUpdateTableData);
  }

  /**
   * When an event asks the table to update the table data
   */
  onUpdateTableData = () => {
    this.onUpdate({
      refresh: true,
    });
  };

  onSelectMenuItem = (itemName, _rowId) => {
    const {
      rows,
    } = this.state;

    const row = find(rows, { _rowId });

    if (!row) {
      console.error('Row not found matching ID', _rowId);
      return;
    }

    switch (itemName) {
      case 'archive': {
        this.setState({
          confirm: true,
          confirmAction: itemName,
          confirmRow: row,
          confirmTitle: `Archive ${C.PROPOSAL}`,
          confirmText: `Are you sure you want to archive ${row.name}?`,
          confirmApproveColor: 'error',
        });

        break;
      }

      case 'unarchive': {
        this.setState({
          confirm: true,
          confirmAction: itemName,
          confirmRow: row,
          confirmTitle: `Unarchive ${C.PROPOSAL}`,
          confirmText: `Are you sure you want to unarchive ${row.name}?`,
          confirmApproveColor: 'warning',
        });

        break;
      }

      default:
        break;
    }
  };

  onConfirmModal = async () => {
    const {
      collection,
      onRemove,
    } = this.props;

    const {
      confirmAction,
      confirmRow: {
        _id,
      },
    } = this.state;

    if (!_id) {
      console.error('onConfirmModal no row ID');
      return;
    }

    switch (confirmAction) {
      case 'archive': {
        const response = await API.Flight.deactivate(_id);

        const successText = 'FLIGHT.DEACTIVATED';
        const success = get(response, '[0].type') === successText;

        if (success) {
          // if using a local collection, remove from the table
          if (collection.length > 0) {
            const items = collection.filter(item => item._id === _id);

            items.forEach((item) => {
              // remove the item from the table
              onRemove(item);
            });
          }

          // tell listening components to update the counts and table data
          this.onUpdate({
            refresh: true,
          });
        }

        break;
      }

      case 'unarchive': {
        const response = await API.Flight.reactivate({
          _id,
        });

        const successText = 'FLIGHT.REACTIVATED';
        const success = get(response, '[0].type') === successText;

        if (success) {
          // tell listening components to update the counts and table data
          this.onUpdate({
            refresh: true,
          });
        }

        break;
      }

      default:
        break;
    }

    this.resetConfirmModal();
  };

  /**
   * When the Bulk Archive Completes
   */
  onBulkArchive = () => {
    // tell listening components to update the counts and table data
    this.onUpdate({
      refresh: true,
    });
  };

  /**
   * Reset to Default Settings
   */
  onReset = () => {
    const {
      defaultSortBy,
      pageSize,
    } = this.props;

    this.onUpdate({
      refresh: true,
      data: {
        sortBy: defaultSortBy,
        pageSize: pageSize || 50,
      },
    });
  };

  onUpdate = ({
    data,
    refresh = false,
  }) => {
    if (refresh) {
      // refresh the table data
      this.setState(data, this.getData);
    } else {
      this.setState(data);
    }
  };

  removeEmptySegmentsFromAttached = (rows) => {
    const {
      filters,
    } = this.state;

    let updatedRows = rows;

    if (filters.adProvider === 'vibenomics' && filters.missingCreatives !== undefined) {
      if (filters.missingCreatives === 'false') {
        updatedRows = rows.filter(row => {
          if (row.missingCreatives === undefined) {
            return false;
          }
          return true;
        });
      }
    }

    return updatedRows;
  };

  getData = async (config = {}) => {
    const {
      props,
      state,
    } = this;

    if (!state.loading && !config.export) {
      this.setState({
        loading: true,
      });
    }

    config.pageSize = state.filters.missingCreatives?.toString() === 'false' ? -1 : state.pageSize;

    const {
      rows,
      filters,
      totalItems,
    } = await getData({
      props,
      state,
      config,
    });

    if (rows) {
      const filteredRows = this.removeEmptySegmentsFromAttached(rows);
      const filteredRowCount = state.filters.missingCreatives?.toString() === 'false'
        ? filteredRows.length : totalItems;
      this.setState({
        loading: false,
        rows: filteredRows,
        totalItems: filteredRowCount,
      });

      props.onFetchComplete({
        rows: filteredRows,
        filters,
        totalItems: filteredRowCount,
      });
    }
  };

  /**
   * Get the custom class for the column
   */
  getCustomClassName = ({
    startDate,
  }) => {
    const currentDate = moment().startOf('day');
    const started = moment(startDate).isBefore(currentDate);
    
    return started
      ? 'started'
      : null;
  };

  /**
   * Reset the confirm modal data
   */
  resetConfirmModal = () => {
    this.setState({
      confirm: false,
    });
  };

  // parse content for company multiselect
  getCompaniesString = (value) => {
    let tooltipTitle = '';
    value.forEach((option) => {
      tooltipTitle += `${option.companyName}, `;
    });
    return tooltipTitle.slice(0, -2);
  };

  // get string value for Missing Creative
  getCreativeString = (value, row) => {
    const adProvider = get(row, 'adProvider', '');

    if (adProvider !== 'vibenomics') {
      return 'N/A';
    }
    switch (value) {
      case true:
        return 'Missing';

      case false:
        return 'Attached';

      default:
        return 'Missing';
    }
  };

  renderCell = ({
    column,
    row,
  }) => {
    // get the attribute with data for the cell
    const attr = column.valueAttr || column.searchAttr;
    const value = get(row, attr, '');
    const customClassName = row.status === 'awaiting-approval'
      ? this.getCustomClassName({
        startDate: row.startDate,
      })
      : '';

    switch (column.name) {
      case 'Select': {
        const {
          highlight,
          onSelectFlight,
        } = this.props;

        const rowId = get(row, '_id');
        const selectedId = get(highlight, '[0]._id');

        return (
          <div
            className="cell-content"
            style={{
              textAlign: 'center',
            }}
          >
            <VibeButton
              text="Select"
              color="primary"
              disabled={rowId === selectedId}
              onClick={() => {
                onSelectFlight(row);
              }}
            />
          </div>
        );
      }

      case 'Name': {
        const isViewOnly = row.partnerAccessLevel === 'view-all'
          || row.partnerAccessLevel === 'view-limited'
          || row.partnerAccessLevel === 'none';

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                flexGrow: 1,
                overflow: 'hidden',
              }}
            >
              <CellTooltip title={value}>
                <div
                  className="cell-content"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {value}
                </div>
              </CellTooltip>
            </div>

            {isViewOnly && (
              <LabelCell
                value="View-Only"
                icon={viEye}
                background={color.primary16}
                color={color.primary}
                inline
              />
            )}
          </div>
        );
      }

      case `${C.PROPOSAL} Type`: {
        const orderType = find(orderTypeList(), { value });

        if (!orderType) {
          return (
            <CellTooltip title={value}>
              <div className={classNames('cell-content', customClassName)}>
                {value}
              </div>
            </CellTooltip>
          );
        }

        return (
          <TextCell
            className={customClassName}
            text={orderType.label}
            icon={orderType.icon}
          />
        );
      }

      case 'Company':
        return (
          <CellTooltip title={this.getCompaniesString(value)}>
            <div className={classNames('cell-content', customClassName)}>
              {this.getCompaniesString(value)}
            </div>
          </CellTooltip>
        );

      case 'Status':
        return (
          <CellTooltip title={value}>
            <div
              className={classNames('cell-content', customClassName)}
              style={{
                textTransform: 'capitalize',
              }}
            >
              <FlightStatus
                status={value}
              />
            </div>
          </CellTooltip>
        );

      case 'Creative':
        return (
          <CellTooltip title={this.getCreativeString(value, row)}>
            <div
              className={classNames('cell-content', customClassName)}
              style={{
                textTransform: 'capitalize',
              }}
            >
              <MissingCreatives
                status={this.getCreativeString(value, row)}
              />
            </div>
          </CellTooltip>
        );

      case 'Start Date':
      case 'End Date':
      case 'Created Date':
        return (
          <TimestampCell
            className={customClassName}
            time={value}
            format="YYYY-MM-DD"
          />
        );

      case 'Ad Provider':
        return (
          <div className={classNames('cell-content', customClassName)}>
            {find(adProviderList(), { value }).label}
          </div>
        );

      case 'Currency': {
        const {
          currencies,
        } = this.props;

        return (
          <div className={classNames('cell-content', customClassName)}>
            {find(currencies, { key: value })?.label || value}
          </div>
        );
      }

      case 'Approver': {
        const approvers = get(row, 'partnerApproval.approvers', []);

        return (
          <ApproversCell
            approvers={approvers}
          />
        );
      }

      case 'Approver Setting': {
        const approvalStyle = get(row, 'partnerApproval.style');
        let approvalSetting = '';

        if (approvalStyle === 'unanimous') {
          approvalSetting = 'All approvers must approve';
        } else if (approvalStyle === 'n-required') {
          approvalSetting = 'Only one approver must approve';
        } else if (approvalStyle === 'anyone') {
          approvalSetting = 'Anyone can approve';
        }

        return (
          <CellTooltip title={value}>
            <div className={classNames('cell-content', customClassName)}>
              {approvalSetting}
            </div>
          </CellTooltip>
        );
      }

      default:
        return (
          <CellTooltip title={value}>
            <div className={classNames('cell-content', customClassName)}>
              {value}
            </div>
          </CellTooltip>
        );
    }
  };

  render() {
    const {
      className,
    } = this.props;

    const {
      columnsDef,
      columnNames,
      rows,
      loading,
      totalItems,
      active,
      pageNumber,
      pageSize,
      sortBy,
      filters,
      confirm,
      confirmTitle,
      confirmText,
      confirmApproveColor,
      currenciesFetched,
    } = this.state;

    if (!currenciesFetched) {
      return null;
    }

    return (
      <div className={classNames('Table', 'TableFlights', className)}>
        <VibeTable
          {...this.props}
          tableId={tableId}
          columnsDef={columnsDef}
          columnNames={columnNames}
          rows={rows}
          loading={loading}
          permissionPrefix={permissionPrefix}
          sortBy={sortBy}
          filters={filters}
          totalItems={totalItems}
          active={active}
          pageNumber={pageNumber}
          pageSize={pageSize}
          renderCell={this.renderCell}
          onSelectMenuItem={this.onSelectMenuItem}
          onBulkArchive={this.onBulkArchive}
          onReset={this.onReset}
          onUpdate={this.onUpdate}
        />

        <VibeModal
          show={confirm}
          type="confirm"
          confirmProps={{
            color: confirmApproveColor,
          }}
          title={confirmTitle}
          text={confirmText}
          onConfirm={this.onConfirmModal}
          onClose={this.resetConfirmModal}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    currencies: state.currency.list,
  };
}

const mapDispatchToProps = {
  getCurrencies,
};

TableFlights.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  ...tablePropsType,
  onSelectFlight: PropTypes.func,
};

TableFlights.defaultProps = {
  ...tablePropsDefault,
  onSelectFlight: () => {},
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TableFlights));
